import type { SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import type { UntypedFormControl } from '@angular/forms';

import type { Attribute } from '@recruitee/http-types';
import { TranslatePipe } from '@recruitee/i18n';
import type { SelectOption } from '@recruitee/user-interface';
import {
  InputAttributesComponent,
  RtStatefulComponent,
  RtUIAttributesTranslations,
} from '@recruitee/user-interface';
import { includesWithTrim } from '@recruitee/utils';

import { Destroyable, takeUntilDestroyed } from '../../common';

export type SimpleMultiselectOption = SelectOption<string>;

type State = {
  attributes: Attribute[];
  optionsByValue: { [value: string]: SelectOption<string> };
  query: string;
};

const UI_ATTRIBUTES_TRANSLATIONS = {
  rtiSimpleMultiselect: {
    placeholder: 'rtu.ui.select.none',
  },
};

// Wraps rt-input-attributes to build a multiselect
// Limited to `SelectOption<string>` due to how we reuse the rt-input-attributes component
@Component({
  selector: 'rti-simple-multiselect',
  templateUrl: './simple-multiselect.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: RtUIAttributesTranslations, useValue: UI_ATTRIBUTES_TRANSLATIONS }],
  imports: [InputAttributesComponent, TranslatePipe],
})
@Destroyable()
export class SimpleMultiselectComponent extends RtStatefulComponent<State> {
  @Input() public control: UntypedFormControl;
  @Input() public options: SimpleMultiselectOption[];

  constructor(protected cdRef: ChangeDetectorRef) {
    super(cdRef);
    this.createState({ attributes: [], optionsByValue: {}, query: '' });
  }

  public ngOnInit(): void {
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this))
      .subscribe(() => this.cdRef.detectChanges());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      const attributes = changes.options.currentValue.map((option: SelectOption<string>) =>
        this.attributeFromOption(option),
      );
      const optionsByValue = changes.options.currentValue.reduce(
        (acc: { [key: string]: SelectOption<string> }, option: SelectOption<string>) => {
          acc[option.value!] = option;
          return acc;
        },
        {},
      );
      this.setState({ ...this.state, attributes, optionsByValue });
    }
  }

  public attributeAdded(attribute: Attribute): void {
    const option = this.optionFromAttribute(attribute);
    const newValue = this.control.value.concat(option.value);
    this.setValue(newValue);
  }

  public attributeDeleted(attribute: Attribute): void {
    const option = this.optionFromAttribute(attribute);
    const newValue = this.control.value.filter((value: any) => value !== option.value);
    this.setValue(newValue);
  }

  public clearAttributes(): void {
    this.setValue([]);
  }

  public get filteredAttributes(): Attribute[] {
    return this.state.attributes.filter(attribute => {
      const option = this.optionFromAttribute(attribute);
      return (
        !this.control.value.includes(option.value) &&
        includesWithTrim(option.label, this.state.query)
      );
    });
  }

  public get selectedAttributes(): Attribute[] {
    return this.control.value
      .map((value: string) => this.options.find(option => option.value === value))
      .map((option: SimpleMultiselectOption) => this.attributeFromOption(option));
  }

  public attributeQueried(query: string): void {
    this.setState({ ...this.state, query });
  }

  private setValue(value: string[]): void {
    this.control.setValue(value);
    this.control.markAsDirty();
  }

  private optionFromAttribute(attribute: Attribute): SimpleMultiselectOption {
    return this.state.optionsByValue[attribute.id!];
  }

  private attributeFromOption(option: SimpleMultiselectOption): Attribute {
    return { id: option.value!, value: option.label };
  }
}
